// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Calendar from "@insite/mobius/Icons/Calendar";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AlertCircle from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleLine";
import { loadingDotsStyles } from "@store/Themes/common/mixins/typography";
import Status, { StatusProps } from "@store/Library/Components/Status";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { is } from "immer/dist/internal";
import React, { useEffect, useMemo, useState } from "react";
import { css } from "styled-components";

export interface LeadTimeModel {
    isSpecialItem?: boolean;
    plant?: string;
    leadTimeDays?: number;
    minimumOrderQuantity?: number;
}

interface OwnProps extends ExpandStyles<ProductLeadTimeStyles> {
    showShortLeadTimeDesc?: boolean;
    overrideLT?: LeadTimeModel;
    isProductContext?: boolean;
    displayLabel?: boolean;
    displaySeparator?: boolean;
}

type Props = OwnProps & HasProduct;

export interface ProductLeadTimeStyles {
    loadingText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    statusStyles?: StatusProps;
    leadTimeMessage?: LeadTimeMessage;
    calendarTooltipProps?: TooltipPresentationProps;
    additionalInfoLabel?: TypographyPresentationProps;
    additionalInfoText?: TypographyPresentationProps;
    pipeStyles?: TypographyPresentationProps;
    backgroundTooltip?: InjectableCss;
}

interface LeadTimeMessage {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export const productLeadTimeStyles: ProductLeadTimeStyles = {
    loadingText: {
        css: loadingDotsStyles(),
    },
    statusStyles: {
        extendedStyles: {
            wrapper: {
                css: css`
                    flex-grow: 0;
                    & > div {
                        display: flex;
                        flex-direction: row;
                    }
                `,
            },
        },
    },
    leadTimeMessage: {
        text: {
            css: css`
                font-size: ${x => x.theme.design.typography.size.smaller};
                margin-left: ${pxToRem(4)};
                text-wrap: auto;
            `,
        },
        tooltip: {
            iconProps: {
                src: AlertCircle,
            },
        },
    },
    calendarTooltipProps: {
        cssOverrides: {
            tooltipWrapper: css`
                ${x => x.theme.tooltip.defaultProps?.cssOverrides?.tooltipWrapper}
                margin-left: 0;
                text-wrap: auto;
            `,
        },
        iconProps: {
            src: Calendar,
            css: css`
                margin-left: 0;
            `,
        },
    },
    additionalInfoLabel: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    additionalInfoText: {
        css: css`
            margin-left: ${pxToRem(6)};
        `,
    },
    pipeStyles: {
        color: "text.disabled",
        css: css`
            margin: ${pxToRem(0, 6)};
        `,
    },
    backgroundTooltip: {
        css: css`
            background: none;
            width: fit-content;
        `,
    },
};

const ProductLeadTime = ({
    product,
    productInfo,
    extendedStyles,
    overridedStyles,
    showShortLeadTimeDesc = false,
    overrideLT,
    isProductContext = false,
    displayLabel = true,
    displaySeparator = false,
}: Props) => {
    const styles = useExpandProps(productLeadTimeStyles, extendedStyles, overridedStyles);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [leadTime, setLeadTime] = useState<LeadTimeModel>();
    const [leadTimeData] = useState<LeadTimeModel[]>([]);

    let leadTimes;
    let pricing: ProductPriceDto | undefined;
    let defaultPlantNumber: string | undefined;
    let leadTimeObject: any;

    if (isProductContext) {
        leadTimes = product.properties.leadTimes;
        pricing = productInfo.pricing;
        defaultPlantNumber = pricing?.additionalResults?.warehouseName;

        // Receiving data as a JSON string, needs to convert and extract properties
        leadTimeObject = JSON.parse(leadTimes);
    }

    useEffect(() => {
        if (isProductContext) {
            setIsLoading(!pricing);
        }
    }, [pricing, defaultPlantNumber]);

    useEffect(() => {
        if (isProductContext) {
            if (leadTimeObject) {
                leadTimeObject.forEach((leadTime: any) =>
                    leadTimeData.push({
                        isSpecialItem: leadTime.IsSpecialItem,
                        leadTimeDays: leadTime.LeadTime,
                        minimumOrderQuantity: leadTime.MinimumOrderQuantity,
                        plant: leadTime.Plant,
                    }),
                );
            }
        }
    }, [leadTimeObject]);

    useEffect(() => {
        if (isProductContext) {
            setLeadTime(defaultPlantNumber ? leadTimeData.find(x => x.plant === defaultPlantNumber) : leadTimeData[0]);
        }
    }, [defaultPlantNumber]);

    useEffect(() => {
        if (overrideLT) {
            setLeadTime(overrideLT);
        }
    }, [overrideLT]);

    if (!leadTimes && isProductContext) {
        return null;
    }

    const statusTooltipStyles = useMemo(() => {
        return isProductContext
            ? mergeProps<ProductLeadTimeStyles["calendarTooltipProps"]>(styles.calendarTooltipProps, {
                  iconProps: {
                      color: `${
                          !leadTime || (overrideLT && Number.isNaN(overrideLT?.leadTimeDays))
                              ? "neutral"
                              : leadTime.isSpecialItem
                              ? "warning"
                              : "success"
                      }`,
                  },
              })
            : styles.calendarTooltipProps;
    }, [isProductContext, styles.calendarTooltipProps, leadTime, overrideLT]);

    const alternateStyle = useMemo(() => {
        return !isProductContext
            ? mergeProps<ProductLeadTimeStyles["backgroundTooltip"]>(styles.backgroundTooltip, {
                  css: css`
                      background: ${!leadTime || (overrideLT && Number.isNaN(overrideLT?.leadTimeDays))
                          ? getColor("neutral")
                          : leadTime.isSpecialItem
                          ? getColor("warning")
                          : getColor("success")};
                      border-radius: 50%;
                      padding: ${pxToRem(1)};
                  `,
              })
            : styles.backgroundTooltip;
    }, [isProductContext, styles.backgroundTooltip, leadTime, overrideLT]);
    const messageStyles = styles.leadTimeMessage || {};

    const statusType = leadTime?.isSpecialItem ? "yellow" : "green";

    const tooltipText = leadTime?.isSpecialItem
        ? siteMessage("Product_LeadTimeTooltipSpecialProduct")
        : siteMessage("Product_LeadTimeTooltipRegularProduct");

    const weekWording = leadTime?.leadTimeDays === 1 ? translate("week") : translate("weeks");

    return (
        <StyledWrapper {...styles.wrapper}>
            {displaySeparator && <Typography {...styles.pipeStyles}>|</Typography>}
            {isLoading && (
                <Status showIcon {...styles.statusStyles}>
                    <Typography {...styles.loadingText}>&nbsp;</Typography>
                </Status>
            )}
            {!isLoading &&
                (!leadTime || (overrideLT && Number.isNaN(overrideLT?.leadTimeDays)) ? (
                    <Status {...styles.statusStyles}>
                        <Tooltip
                            {...statusTooltipStyles}
                            text={siteMessage("ProductInfo_LeadTimeIconNaMessage") as string}
                        />
                        {showShortLeadTimeDesc ? (
                            <Typography {...messageStyles.text}>
                                {isProductContext ? translate("Contact Customer Service") : translate("N/A")}
                            </Typography>
                        ) : (
                            <Typography {...messageStyles.text}>
                                {siteMessage("Product_LeadTimeUnavailable")}
                            </Typography>
                        )}
                    </Status>
                ) : (
                    <Status type={statusType} {...styles.statusStyles}>
                        {leadTime.isSpecialItem ? (
                            <>
                                {displayLabel ? (
                                    <>
                                        <Tooltip
                                            {...statusTooltipStyles}
                                            text={siteMessage("ProductInfo_LeadTimeIconMessage") as string}
                                        />
                                        <Typography {...messageStyles.text}>
                                            {showShortLeadTimeDesc
                                                ? translate(
                                                      "{0} {1} (MOQ: {2} units)",
                                                      String(leadTime.leadTimeDays),
                                                      weekWording,
                                                      String(leadTime.minimumOrderQuantity),
                                                  )
                                                : translate(
                                                      "Lead Time {0} {1} for minimum order quantity of {2} units.",
                                                      String(leadTime.leadTimeDays),
                                                      weekWording,
                                                      String(leadTime.minimumOrderQuantity),
                                                  )}
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Tooltip
                                            {...statusTooltipStyles}
                                            text={
                                                `${siteMessage("ProductInfo_LeadTimeIconMessage")} ${translate(
                                                    "(MOQ: {0} units)",

                                                    String(leadTime.minimumOrderQuantity),
                                                )}` as string
                                            }
                                        />
                                        <Typography {...messageStyles.text}>
                                            {translate("{0} {1}", String(leadTime.leadTimeDays), weekWording)}
                                        </Typography>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <Tooltip
                                    {...statusTooltipStyles}
                                    text={siteMessage("ProductInfo_LeadTimeIconMessage") as string}
                                />
                                <Typography {...messageStyles.text}>
                                    {showShortLeadTimeDesc ? (
                                        translate("{0} {1}", String(leadTime.leadTimeDays), weekWording)
                                    ) : (
                                        <>
                                            <Typography {...styles.additionalInfoLabel}>
                                                {displayLabel && `${translate("Lead Time")}${translate(":")}`}
                                            </Typography>
                                            <Typography {...styles.additionalInfoText}>
                                                {`${translate("{0} {1}", String(leadTime.leadTimeDays), weekWording)}`}
                                            </Typography>
                                        </>
                                    )}
                                </Typography>
                            </>
                        )}
                        <StyledWrapper {...alternateStyle}>
                            <Tooltip {...messageStyles?.tooltip} text={tooltipText as string} />
                        </StyledWrapper>
                    </Status>
                ))}
        </StyledWrapper>
    );
};

export default withProduct(ProductLeadTime);
